import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Calculator, TrendingDown, TrendingUp, Sparkles, ArrowRight, Loader2 } from "lucide-react";

const TYPOLOGIES = ["T0", "T1", "T2", "T3", "T4", "T5+"];

const fmtEur = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

function flagPresentation(flag) {
  switch (flag) {
    case "oportunidade":
      return { label: "Bem abaixo do mercado", color: "text-[#103C4A] bg-[#103C4A]/10", Icon: Sparkles };
    case "abaixo":
      return { label: "Abaixo do mercado", color: "text-[#5C3A00] bg-[#F0EAD2]", Icon: TrendingDown };
    case "ligeiramente_acima":
      return { label: "Um pouco acima do mercado", color: "text-stone-700 bg-stone-100", Icon: TrendingUp };
    case "acima":
      return { label: "Bem acima do mercado", color: "text-red-800 bg-red-100", Icon: TrendingUp };
    default:
      return { label: "Alinhado com o mercado", color: "text-stone-700 bg-stone-100", Icon: TrendingUp };
  }
}

/**
 * Public price-comparison widget.
 * Props:
 *  - variant: "card" (default, self-contained white card) | "embedded" (no wrapper)
 *  - defaultZone: pre-select a zone slug
 *  - onLead: optional callback with the last {zone, typology, area, target_price} — for
 *    consumers that want to convert the estimate into a lead capture.
 */
export default function PriceEstimator({ variant = "card", defaultZone = "", onLead }) {
  const [zones, setZones] = useState([]);
  const [zoneSlug, setZoneSlug] = useState(defaultZone);
  const [typology, setTypology] = useState("T2");
  const [area, setArea] = useState("");
  const [targetPrice, setTargetPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api
      .get("/zones")
      .then((r) => {
        const list = (r.data || []).filter((z) => z.avg_price_sqm);
        setZones(list);
        if (!defaultZone && list.length > 0) setZoneSlug((s) => s || list[0].slug);
      })
      .catch(() => setZones([]));
  }, [defaultZone]);

  const disabled = useMemo(
    () => !zoneSlug || !typology || !area || Number(area) <= 0 || loading,
    [zoneSlug, typology, area, loading]
  );

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const payload = {
        zone_slug: zoneSlug,
        typology,
        area: Number(area),
      };
      const tp = Number(targetPrice);
      if (targetPrice && tp > 0) payload.target_price = tp;
      const r = await api.post("/market/estimate", payload);
      setResult(r.data);
      if (onLead) onLead(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível estimar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  const wrapper =
    variant === "card"
      ? "rounded-3xl border border-stone-200 bg-white p-6 md:p-8 shadow-[0_10px_40px_rgba(16,60,74,0.06)]"
      : "";

  const comp = result?.comparison;
  const pres = comp ? flagPresentation(comp.flag) : null;

  return (
    <div className={wrapper} data-testid="price-estimator">
      <div className="flex items-start gap-4">
        <div className="hidden md:flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#103C4A]/10 text-[#103C4A]">
          <Calculator className="h-6 w-6" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-stone-500">Comparador de preços</div>
          <h3 className="mt-1 font-display text-2xl md:text-3xl text-stone-900 tracking-[-0.02em]">
            Quanto vale um imóvel na sua zona?
          </h3>
          <p className="mt-1 text-sm text-stone-600">
            Escolha zona, tipologia e área. Se quiser, adicione um preço para comparar com o mercado.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Zona</label>
          <select
            data-testid="estimator-zone"
            value={zoneSlug}
            onChange={(e) => setZoneSlug(e.target.value)}
            className="mt-1 w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#103C4A]"
          >
            {zones.length === 0 && <option value="">A carregar…</option>}
            {zones.map((z) => (
              <option key={z.slug} value={z.slug}>
                {z.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Tipologia</label>
          <select
            data-testid="estimator-typology"
            value={typology}
            onChange={(e) => setTypology(e.target.value)}
            className="mt-1 w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#103C4A]"
          >
            {TYPOLOGIES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Área (m²)</label>
          <input
            data-testid="estimator-area"
            type="number"
            min="10"
            step="1"
            placeholder="Ex. 95"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            className="mt-1 w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#103C4A]"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Preço para comparar <span className="normal-case tracking-normal text-stone-400">(opcional)</span>
          </label>
          <input
            data-testid="estimator-target"
            type="number"
            min="0"
            step="1000"
            placeholder="Ex. 220 000"
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            className="mt-1 w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#103C4A]"
          />
        </div>
        <div className="md:col-span-1 flex items-end">
          <button
            type="submit"
            data-testid="estimator-submit"
            disabled={disabled}
            className="btn-primary w-full justify-center disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Estimar"}
            {!loading && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </form>

      {error && (
        <div data-testid="estimator-error" className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div data-testid="estimator-result" className="mt-6 rounded-2xl bg-stone-50 border border-stone-200 p-5 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">Mínimo estimado</div>
              <div className="mt-1 font-display text-2xl text-stone-700">{fmtEur(result.estimate.low)}</div>
            </div>
            <div className="md:border-l md:border-r border-stone-200 md:px-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">Valor central</div>
              <div className="mt-1 font-display text-3xl md:text-4xl text-[#103C4A] tracking-[-0.02em]" data-testid="estimator-mid">
                {fmtEur(result.estimate.mid)}
              </div>
              <div className="text-[11px] text-stone-500">
                {result.typology} · {result.area} m² · {fmtEur(result.target_sqm)}/m²
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500">Máximo estimado</div>
              <div className="mt-1 font-display text-2xl text-stone-700">{fmtEur(result.estimate.high)}</div>
            </div>
          </div>

          {comp && (
            <div className="mt-5 rule-hair" />
          )}
          {comp && (
            <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="estimator-comparison">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.16em] ${pres.color}`} data-testid={`estimator-flag-${comp.flag || "neutro"}`}>
                  <pres.Icon className="h-3.5 w-3.5" />
                  {pres.label}
                </span>
                <div className="text-sm text-stone-600">
                  Preço indicado: <strong className="text-stone-900 font-medium">{fmtEur(comp.target_price)}</strong>{" "}
                  ({fmtEur(comp.target_price_sqm)}/m²) — {comp.diff_percent > 0 ? "+" : ""}
                  {comp.diff_percent}% face à média.
                </div>
              </div>
              <Link
                to="/avaliacao-gratuita"
                data-testid="estimator-cta-avaliacao"
                className="btn-secondary md:shrink-0"
              >
                Avaliação precisa da Zélia <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {!comp && (
            <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="text-sm text-stone-600">
                Referência baseada em <strong className="text-stone-900 font-medium">€/m² actual do mercado</strong>{" "}
                para {result.typology} nesta zona. Para um valor personalizado ao seu imóvel, peça uma avaliação.
              </p>
              <Link
                to="/avaliacao-gratuita"
                data-testid="estimator-cta-avaliacao-empty"
                className="btn-primary md:shrink-0"
              >
                Avaliação gratuita <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      )}

      <p className="mt-4 text-[11px] text-stone-400 leading-relaxed">
        Estimativa indicativa. Valor final depende de estado, andar, exposição, ano e certificações — factores incluídos numa avaliação humana.
      </p>
    </div>
  );
}
